import { useEffect, useState } from 'react';
import { message } from 'antd';
import { AxiosResponse } from 'axios';

// Overload 1: With params
export function useGridData<T, P>(
  fetchFn: (params: P) => Promise<AxiosResponse<T[]>>,
  params: P
): {
  rowData: T[];
  loading: boolean;
  refetch: () => void;
};

// Overload 2: Without params
export function useGridData<T>(
  fetchFn: () => Promise<AxiosResponse<T[]>>
): {
  rowData: T[];
  loading: boolean;
  refetch: () => void;
};

// Actual implementation
export function useGridData<T, P>(
  fetchFn: ((params: P) => Promise<AxiosResponse<T[]>>) | (() => Promise<AxiosResponse<T[]>>),
  params?: P
) {
  const [rowData, setRowData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const res =
        typeof params !== 'undefined'
          ? await (fetchFn as (params: P) => Promise<AxiosResponse<T[]>>)(params)
          : await (fetchFn as () => Promise<AxiosResponse<T[]>>)();
      setRowData(res.data);
    } catch (err) {
      message.error('Failed to load data');
      console.error('Grid fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params)]);

  return { rowData, loading, refetch: loadData };
}
